
  import { defineComponent, onMounted, ref } from 'vue';
  import { Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { downloadUrlToFile } from '@/core/helpers/downloader';
  import { triggerDownload } from '@/core/helpers/trigger-download.util';

  export default defineComponent({
    name: 'newsletter-sender-preview',
    components: {
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const sendMailButton = ref<HTMLButtonElement | null>(null);
      const showArchivedModal = ref(false);
      const archivedHtmlContent = ref<string>('');
      const releaseIdentifier = ref<string | null>(null);
      const approvalStatus = ref<string>('pending'); //current newsletter approval status
      const approvedAt = ref<Date | null>(null); //archived newsletter approved at
      const approvedBy = ref<string | null>(null); //archived newsletter approved by

      const closeArchivedModal = () => {
        showArchivedModal.value = false;
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('newsletterSenderPreview'), [
          translate('settings'),
        ]);
        approvalStatus.value = data.data.status;
      });
      const previewVersion = Number(route.query.version) || 2;
      const { data } = await store.dispatch(
        Actions.GET_NEWSLETTER_PREVIEW,
        previewVersion
      );

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const newsletter = ref<any>(data.data);

      const [errorName] = Object.keys(store.getters.getErrors);
      const error = store.getters.getErrors[errorName];

      const sendToWhatsApp = async () => {
        //call here for whatsapp sending
      };

      const sendToMail = async () => {
        if (sendMailButton.value) {
          sendMailButton.value.disabled = true;
          sendMailButton.value.setAttribute('data-kt-indicator', 'on');
        }
        try {
          await store.dispatch(Actions.SEND_TO_SUBSCRIBERS, {
            release: newsletter.value.release,
          });
          Swal.fire({
            text: translate('SUCCESSFULLY_SENT_NEWSLETTER'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
        } catch (error) {
          Swal.fire({
            text: translate('Failure sending newsletter'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn btn-light-danger',
            },
          });
        } finally {
          if (sendMailButton.value) {
            sendMailButton.value.disabled = false;
            sendMailButton.value.removeAttribute('data-kt-indicator');
          }
        }
      };

      const approveNewsletter = async () => {
        try {
          await store.dispatch(Actions.APPROVE_NEWSLETTER, {
            releaseIdentifier: releaseIdentifier.value,
          });

          Swal.fire({
            text: translate('SUCCESSFULLY_APPROVED_NEWSLETTER'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            closeArchivedModal;
            router.go(0);
          });
          showArchivedModal.value = false;
        } catch (err) {
          Swal.fire({
            text: translate('Newsletter already approved'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn btn-light-danger',
            },
          });
        }
      };

      const onSubmitArchive = async () => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const results = await store.dispatch(Actions.ARCHIVE_NEWSLETTER, {
          htmlContent: newsletter.value.htmlContent,
        });

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          submitButton.value?.removeAttribute('data-kt-indicator');
          submitButton.value!.disabled = false;
        } else {
          const pdfUrl =
            results?.data?.data?.s3_pdf_url ||
            results?.data?.s3_pdf_url ||
            null;

          if (pdfUrl) {
            const suggestedName = `newsletter-${
              releaseIdentifier.value || 'archive'
            }.pdf`;
            await triggerDownload(pdfUrl, suggestedName);
          } else {
            console.warn('No s3_pdf_url returned from archive response.');
          }

          const response = await store.dispatch(
            Actions.GET_ARCHIVED_NEWSLETTER_PREVIEW,
            results.data.release
          );

          archivedHtmlContent.value = response.data.data.html_content;
          showArchivedModal.value = true;

          releaseIdentifier.value = results.data.data.release;
          approvalStatus.value = results.data.data.status;
          approvedAt.value = results.data.data.approved_at;
          approvedBy.value = results.data.data.approved_by;
        }
        if (submitButton.value) {
          submitButton.value.disabled = false;
          submitButton.value.removeAttribute('data-kt-indicator');
        }
      };

      const formatDate = (date: string | Date) => {
        const d = new Date(date);
        return `${d.toISOString().slice(0, 10)} ${d
          .getHours()
          .toString()
          .padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitArchive,
        submitButton,
        translate,
        goBack,
        newsletter,
        can,
        sendToWhatsApp,
        sendToMail,
        sendMailButton,
        archivedHtmlContent,
        showArchivedModal,
        closeArchivedModal,
        approveNewsletter,
        releaseIdentifier,
        approvalStatus,
        approvedAt,
        approvedBy,
        formatDate,
        error,
      };
    },
  });
